import type { StructureRef } from './proposalStructureRefs';

/** Inline token: [[struct:dealId:nodeSetId|Display Name]] */
export const STRUCTURE_MENTION_TOKEN_RE =
  /\[\[struct:([^:\]|]+):([^|\]]+)\|([^\]]*)\]\]/g;

export type ParsedStructureMentionToken = {
  dealId: string;
  structureId: string;
  label: string;
  fullMatch: string;
  index: number;
};

export function buildStructureMentionToken(params: {
  dealId: string;
  structureId: string;
  name: string;
}): string {
  const dealId = params.dealId.trim();
  const structureId = params.structureId.trim();
  const name = (params.name || 'Structure').trim();
  if (!dealId || !structureId) return name;
  const safeLabel = name.replace(/\]\]/g, '');
  return `[[struct:${dealId}:${structureId}|${safeLabel}]]`;
}

export function parseStructureMentionTokens(text: string): ParsedStructureMentionToken[] {
  const out: ParsedStructureMentionToken[] = [];
  if (!text) return out;
  const re = new RegExp(STRUCTURE_MENTION_TOKEN_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    out.push({
      dealId: m[1].trim(),
      structureId: m[2].trim(),
      label: m[3],
      fullMatch: m[0],
      index: m.index,
    });
  }
  return out;
}

export function resolveStructureMentionIndex(
  structureId: string,
  structures: StructureRef[] | undefined,
): { index: number; total: number } | null {
  if (!structures?.length || !structureId) return null;
  const idx = structures.findIndex((s) => s.id === structureId);
  if (idx < 0) return null;
  return { index: idx + 1, total: structures.length };
}

export function structureMentionHoverTitle(
  displayName: string,
  resolved: { index: number; total: number } | null,
  options?: { missing?: boolean },
): string {
  const name = displayName.trim() || 'Structure';
  if (options?.missing) {
    return `${name} — structure not found on this project`;
  }
  if (!resolved || resolved.total <= 0) {
    return `${name} — structure on this project`;
  }
  return `${name} — structure ${resolved.index} of ${resolved.total} on this project`;
}

export function resolveStructureMentionDisplay(
  token: Pick<ParsedStructureMentionToken, 'structureId' | 'label'>,
  structures: StructureRef[] | undefined,
): { displayName: string; hover: string } {
  const hit = structures?.find((s) => s.id === token.structureId);
  const displayName = hit?.name?.trim() || token.label.trim() || 'Structure';
  const resolved = resolveStructureMentionIndex(token.structureId, structures);
  const hover = structureMentionHoverTitle(displayName, resolved, {
    missing: !hit && !!structures?.length,
  });
  return { displayName, hover };
}

export function filterStructuresForQuery(
  structures: StructureRef[],
  query: string,
): StructureRef[] {
  const q = query.trim().toLowerCase();
  if (!q) return structures;
  return structures.filter((s) => s.name.toLowerCase().includes(q));
}

export type StructureMentionTextSegment =
  | { kind: 'text'; text: string }
  | {
      kind: 'mention';
      token: ParsedStructureMentionToken;
    };

/** Split plain message into text runs and structure tokens (for rendering). */
export function splitMessageWithStructureMentions(text: string): StructureMentionTextSegment[] {
  if (!text) return [];
  const segments: StructureMentionTextSegment[] = [];
  const re = new RegExp(STRUCTURE_MENTION_TOKEN_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      segments.push({ kind: 'text', text: text.slice(last, m.index) });
    }
    segments.push({
      kind: 'mention',
      token: {
        dealId: m[1].trim(),
        structureId: m[2].trim(),
        label: m[3],
        fullMatch: m[0],
        index: m.index,
      },
    });
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    segments.push({ kind: 'text', text: text.slice(last) });
  }
  return segments.length ? segments : [{ kind: 'text', text }];
}

/** For plain-text split (capture group preserves tokens). */
export const STRUCTURE_MENTION_PLAIN_SPLIT_RE =
  /(\[\[struct:[^:\]|]+:[^|\]]+\|[^\]]*\]\])/g;

export function enrichStructureMentionHtmlTitles(
  html: string,
  options: {
    defaultDealId?: string;
    defaultStructures?: StructureRef[];
    structuresByDealId?: Map<string, StructureRef[]>;
  },
): string {
  if (!html || typeof document === 'undefined') return html;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const spans = tpl.content.querySelectorAll('[data-structure-id][data-deal-id]');
  spans.forEach((node) => {
    const parsed = parseStructureMentionFromElement(node);
    if (!parsed) return;
    const list =
      options.structuresByDealId?.get(parsed.dealId)
      ?? (parsed.dealId === options.defaultDealId ? options.defaultStructures : undefined);
    const { displayName, hover } = resolveStructureMentionDisplay(parsed, list);
    node.setAttribute('title', hover);
    if (displayName) node.textContent = displayName;
  });
  return tpl.innerHTML;
}

export function parseStructureMentionFromElement(el: Element): {
  dealId: string;
  structureId: string;
  label: string;
} | null {
  const structureId = el.getAttribute('data-structure-id')?.trim();
  const dealId = el.getAttribute('data-deal-id')?.trim();
  if (!structureId || !dealId) return null;
  const label =
    el.getAttribute('data-label')?.trim()
    || (el.textContent ?? '').trim()
    || 'Structure';
  return { dealId, structureId, label };
}
